import { Injectable, inject } from '@angular/core';
import { Observable, shareReplay, tap } from 'rxjs';
import { ApiService } from './api.service';
import {
  PycnometerProfile,
  PycnometerProfileSummary,
  SavePycnometerProfile
} from '@models/pycnometer-profile.interface';

@Injectable({
  providedIn: 'root'
})
export class PycnometerProfilesService {
  private readonly api = inject(ApiService);
  private listCache$?: Observable<PycnometerProfileSummary[]>;
  private readonly detailCache = new Map<number, Observable<PycnometerProfile>>();

  getList(forceRefresh = false): Observable<PycnometerProfileSummary[]> {
    if (forceRefresh || !this.listCache$) {
      this.listCache$ = this.api
        .get('pycnometerprofiles')
        .pipe(shareReplay(1));
    }

    return this.listCache$;
  }

  getById(id: number, forceRefresh = false): Observable<PycnometerProfile> {
    if (forceRefresh || !this.detailCache.has(id)) {
      const request$ = this.api
        .get(`pycnometerprofiles/${id}`)
        .pipe(shareReplay(1));
      this.detailCache.set(id, request$);
    }

    return this.detailCache.get(id)!;
  }

  create(dto: SavePycnometerProfile): Observable<PycnometerProfile> {
    return this.api
      .post('pycnometerprofiles', dto)
      .pipe(tap(saved => this.invalidateCache(saved.id)));
  }

  update(id: number, dto: SavePycnometerProfile): Observable<PycnometerProfile> {
    return this.api
      .put(`pycnometerprofiles/${id}`, dto)
      .pipe(tap(saved => this.invalidateCache(saved.id)));
  }

  delete(id: number): Observable<void> {
    return this.api
      .delete(`pycnometerprofiles/${id}`)
      .pipe(tap(() => this.invalidateCache(id)));
  }

  private invalidateCache(id?: number): void {
    this.listCache$ = undefined;
    if (id !== undefined) {
      this.detailCache.delete(id);
    }
  }
}
